import { inject, Injectable } from "@angular/core";
import { Store } from "@ngxs/store";
import { OrganizationState } from "@vp/data-access/organization";
import { TagsState } from "@vp/data-access/tags";
import { ContentData, OrganizationContentType, Tag, TagType } from "@vp/models";
import { IAssignmentService } from "@vp/shared/assignments/models";
import { DialogData, DialogFactoryService } from "@vp/shared/components/generic-dialog";
import { filterNullMap } from "@vp/shared/operators";
import { combineLatest, Observable } from "rxjs";
import { map, take, withLatestFrom } from "rxjs/operators";
import { ContentFilterState } from "../state+/content-filter.state";

@Injectable()
export class ContentTagsAssignmentService implements IAssignmentService {
  tags$: Observable<Tag[]> = inject(Store).select(TagsState.tags);
  workingCopy$: Observable<ContentData | null> = inject(Store).select(
    ContentFilterState.workingCopy
  );
  tagTypes$: Observable<TagType[]> = inject(Store).select(OrganizationState.tagTypes);
  contentTypes$: Observable<OrganizationContentType[]> = inject(Store).select(
    OrganizationState.contentTypes
  );

  constructor(private dialogFactoryService: DialogFactoryService) {}

  assignedTagIds$ = this.workingCopy$.pipe(
    filterNullMap(),
    map((c: ContentData) => c?.tags ?? [])
  );

  assignableEntities$ = combineLatest([this.assignedTagIds$, this.tags$]).pipe(
    map(([assigned, all]: [string[], Tag[]]) => all.filter(tag => assigned.indexOf(tag.tagId) < 0))
  );

  assignableTagTypes$ = this.workingCopy$.pipe(
    filterNullMap(),
    withLatestFrom(this.tagTypes$, this.contentTypes$),
    map(
      ([contentData, tagTypes, contentTypes]: [
        ContentData,
        TagType[],
        OrganizationContentType[]
      ]) => getAssignableTagTypes(contentData, tagTypes, contentTypes)
    )
  );

  public open(dialogData: DialogData): void {
    this.dialogFactoryService
      .open(dialogData, {
        width: "70vw",
        minHeight: "90vh",
        disableClose: false
      })
      .closed$.pipe(take(1))
      .subscribe();
  }
}

const getAssignableTagTypes = (
  contentData: ContentData,
  tagTypes: TagType[],
  contentTypes: OrganizationContentType[]
) => {
  const selectedContentType = contentTypes.find(ct => ct.friendlyId === contentData.contentTypeId);
  const assignableTagTypes = selectedContentType?.assignableTagTypesToContent ?? [];
  return tagTypes.filter(tt => assignableTagTypes.includes(tt.friendlyId));
};
